import {onBeforeUnmount, onMounted} from "vue";

const MIN_WIDTH = 280;
const MIN_HEIGHT = 240;

interface ResizerEdge {
  cls: string;
  horizontal: -1 | 0 | 1;
  vertical: 0 | 1;
}

const EDGES: ResizerEdge[] = [
  {cls: "nama-resizer-bottom", horizontal: 0, vertical: 1},
  {cls: "nama-resizer-right", horizontal: -1, vertical: 0},
  {cls: "nama-resizer-left", horizontal: 1, vertical: 0},
  {cls: "nama-resizer-bottom-right", horizontal: -1, vertical: 1},
  {cls: "nama-resizer-bottom-left", horizontal: 1, vertical: 1},
];

const MAX_ATTACH_ATTEMPTS = 60;

export function useResizableFilterPopup(getRootEl: () => HTMLElement | null | undefined) {
  let popup: HTMLElement | null = null;
  let cancelled = false;
  const disposers: Array<() => void> = [];

  function findPopup(): HTMLElement | null {
    const target = getRootEl()?.closest(".ag-menu.ag-filter-menu") as HTMLElement | null;
    return target && target.querySelector(".ag-multi-filter") ? target : null;
  }

  function startDrag(event: PointerEvent, edge: ResizerEdge) {
    if (!popup) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = popup.getBoundingClientRect();
    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = rect.width;
    const startHeight = rect.height;
    const startLeft = parseFloat(popup.style.left) || rect.left;
    const maxWidth = window.innerWidth * 0.95;
    const maxHeight = window.innerHeight * 0.85;

    const onMove = (moveEvent: PointerEvent) => {
      if (!popup) return;
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      if (edge.vertical === 1)
        popup.style.height = `${clamp(startHeight + deltaY, MIN_HEIGHT, maxHeight)}px`;
      if (edge.horizontal === 1) {
        popup.style.width = `${clamp(startWidth + deltaX, MIN_WIDTH, maxWidth)}px`;
      } else if (edge.horizontal === -1) {
        const width = clamp(startWidth - deltaX, MIN_WIDTH, maxWidth);
        popup.style.width = `${width}px`;
        popup.style.left = `${startLeft + (startWidth - width)}px`;
      }
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  function attach(attemptsLeft = MAX_ATTACH_ATTEMPTS) {
    if (cancelled) return;
    popup = findPopup();
    if (!popup) {
      if (attemptsLeft > 0)
        requestAnimationFrame(() => attach(attemptsLeft - 1));
      return;
    }
    if (popup.dataset.namaResizerAttached === "true") return;
    popup.dataset.namaResizerAttached = "true";
    const attachedPopup = popup;
    for (const edge of EDGES) {
      const handle = document.createElement("div");
      handle.className = `nama-filter-resizer ${edge.cls}`;
      const onDown = (downEvent: PointerEvent) => startDrag(downEvent, edge);
      handle.addEventListener("pointerdown", onDown);
      attachedPopup.appendChild(handle);
      disposers.push(() => {
        handle.removeEventListener("pointerdown", onDown);
        handle.remove();
      });
    }
    disposers.push(() => delete attachedPopup.dataset.namaResizerAttached);
  }

  onMounted(() => attach());
  onBeforeUnmount(() => {
    cancelled = true;
    disposers.forEach(dispose => dispose());
    disposers.length = 0;
  });
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
